import {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {ClientMemoryApi} from '../../../api/clientMemoryApi';
import {isDeclarableKind} from '../memory/contextPackConstants';
import {
    DOC_SOURCE, MAX_TRANSCRIPT_CHARS, URL_ERROR_KEYS, isRetryableUploadError, uploadErrorKeyFor,
    uploadErrorParamsFor,
} from '../memory/docConstants';
import {
    FILE_KIND, FORMATS_KEY, INGEST_MODE, UPLOAD_CONCURRENCY, classifyFile, readAudioDuration,
    runLimited,
} from '../memory/ingestFiles';

// Largo del título que se deriva del texto pegado. Corto a propósito: es un rótulo para
// encontrarlo en la lista, no un resumen.
const DERIVED_TITLE_MAX = 60;
// Letras latinas, con tildes y eñe: alcanza para decidir si un renglón dice algo o es sólo una hora.
const HAS_LETTER = /[A-Za-zÀ-ɏ]/;

// Un enlace pelado (lo único que hay en el crudo es una URL). Ese caso NO lleva título nuestro:
// el backend busca la página y usa su `<title>` real, que siempre le gana a lo que podamos armar
// desde el FE.
function isBareUrl(text) {
    return /^https?:\/\/\S+$/i.test(text.trim());
}

/**
 * Título para el material que se pega como texto.
 *
 * Sin esto el documento entra como «Documento sin título» —verificado en la app— y una lista de
 * "Documento sin título" repetidos es inservible para encontrar nada. El backend sólo tiene un
 * buen título propio cuando el crudo es un enlace.
 */
export function deriveTitle(text) {
    const body = String(text || '').trim();
    if (!body || isBareUrl(body)) return null;
    // La primera línea CON LETRAS: una transcripción suele arrancar con la hora sola en su renglón
    // («00:00:12»), y ese no es un nombre con el que encontrarla en la lista (QA-alim-N01).
    const lines = body.split('\n').map(l => l.trim()).filter(Boolean);
    const firstLine = lines.find(l => HAS_LETTER.test(l)) || lines[0] || body;
    if (firstLine.length <= DERIVED_TITLE_MAX) return firstLine;
    // Cortar en el último espacio para no partir una palabra al medio.
    const cut = firstLine.slice(0, DERIVED_TITLE_MAX);
    const lastSpace = cut.lastIndexOf(' ');
    return `${(lastSpace > 20 ? cut.slice(0, lastSpace) : cut).trim()}…`;
}

const titleFromFilename = (name) => String(name || '').replace(/\.[^.]+$/, '');
const newRequestKey = () => window.crypto?.randomUUID?.()
    || `memory-${Date.now()}-${Math.random().toString(36).slice(2)}`;

// El envío salió y no sabemos si llegó (se cortó la red). El texto le pide reenviarlo: viaja con la
// MISMA clave, así que si ya había llegado el backend devuelve ese documento en vez de crear otro.
const UNCERTAIN_KEY = 'ia_booster.omni.send_uncertain';
// Cuántos textos sin confirmar se recuerdan. Es memoria de una visita: alcanza con unos pocos.
const MAX_UNCONFIRMED = 20;

export const UPLOAD_STATUS = {UPLOADING: 'uploading', FAILED: 'failed'};

let uploadSeq = 0;
const nextUploadId = () => {
    uploadSeq += 1;
    return `up-${uploadSeq}`;
};

/**
 * Alimentar la memoria de un cliente desde el Recorrido.
 *
 * Un solo camino para las dos zonas de entrada del paso Alimenta —la Barra Total y el orbe—
 * porque son la misma acción con dos gestos. El texto termina en el mismo
 * `createDocument` que usa la pantalla de carga; los archivos binarios (PDF / Word / imagen / audio)
 * en `uploadDocumentFile`, que es multipart. Qué archivo va por cuál camino lo decide
 * `memory/ingestFiles.classifyFile`, la única tabla de admisión del frontend.
 *
 * Un enlace pegado no necesita nada especial: el backend detecta que el crudo es una URL, la
 * busca y estampa `source: URL` solo. Por eso el FE manda siempre MANUAL.
 *
 * **`uploads`** es la lista de archivos en vuelo o fallados, con su progreso — es lo que pinta
 * la tira debajo de la Barra Total. Un archivo de TEXTO también entra
 * (con `progress: null`, indeterminado) para que el chip se comporte igual sea `.txt` o `.pdf`,
 * y para que el motivo de un rechazo del backend quede en el chip y no sólo en un toast que ya
 * se fue.
 *
 * Tiene UN efecto: `beforeunload` mientras haya una subida binaria en vuelo. Cerrar la pestaña con
 * un PDF a mitad de camino lo pierde en silencio; un POST de texto dura menos de un segundo y no
 * lo necesita. No se aborta en unmount: la navegación interna no mata el XHR, el documento se crea
 * igual y aparece en el listado — abortar perdería el archivo sin avisar.
 */
export function useMemoryIngest(clientId, onIngested) {
    const {t} = useTranslation();
    const scopeRef = useRef(clientId);
    scopeRef.current = clientId;
    // Cambiar de cliente puede DESMONTAR la pantalla en vez de re-renderizarla, y entonces
    // `scopeRef` se queda con el cliente viejo: la respuesta que llega tarde pasaba la guarda y el
    // toast «Entró a la memoria» del cliente A aparecía en la pantalla de B (QA-alim-11). Un efecto
    // y no un `null` en `scopeRef`, porque en StrictMode el montaje se simula dos veces.
    const mountedRef = useRef(false);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);
    // La clave de cada envío de texto que todavía no se confirmó, por su contenido. Reenviar el mismo
    // texto —aunque en el medio se hayan mandado otros— reusa su clave, y el backend no duplica.
    const unconfirmedTextRef = useRef(new Map());
    // La clave de cada archivo, por el objeto `File`: «Reintentar» en su chip reusa la misma.
    const fileIntentsRef = useRef(new WeakMap());
    // Cuántos envíos hay EN VUELO, no un booleano. Un gesto puede mandar N archivos a la vez (un
    // drop, el selector `multiple`), y con un booleano el primer `.then` apagaba `sending`: el
    // botón se volvía a habilitar con subidas todavía viajando, y un segundo click mandaba lo que
    // subiendo. El contador sólo llega a cero cuando cerró la última.
    const [inFlight, setInFlight] = useState(0);
    const [uploads, setUploads] = useState([]);
    const sending = inFlight > 0;
    // Cambiar de cliente empieza de cero: ni las claves ni las subidas del anterior le sirven a éste.
    useEffect(() => {
        unconfirmedTextRef.current.clear();
        fileIntentsRef.current = new WeakMap();
        setUploads([]);
        setInFlight(0);
    }, [clientId]);

    const start = useCallback(() => setInFlight(n => n + 1), []);
    // `Math.max` y no `n - 1` a secas: dos caminos cierran el mismo envío (el `.then` y el `.catch`
    // no pueden correr los dos, pero un negativo dejaría `sending` en false con algo todavía vivo).
    const done = useCallback(() => setInFlight(n => Math.max(0, n - 1)), []);

    const removeUpload = useCallback((id) => {
        setUploads(current => current.filter(entry => entry.id !== id));
    }, []);
    const patchUpload = useCallback((id, patch) => {
        setUploads(current => current.map(entry => (entry.id === id ? {...entry, ...patch} : entry)));
    }, []);

    const formats = useCallback(() => t(FORMATS_KEY), [t]);

    const rememberUnconfirmed = useCallback((signature, key) => {
        const unconfirmed = unconfirmedTextRef.current;
        unconfirmed.delete(signature);
        unconfirmed.set(signature, key);
        if (unconfirmed.size > MAX_UNCONFIRMED) unconfirmed.delete(unconfirmed.keys().next().value);
    }, []);

    // El POST de texto: resuelve `{ok, reasonKey}` para que quien lo llame pueda mostrar el motivo
    // donde corresponda (toast, chip). `submitText` es su versión pública con toast incluido.
    const postText = useCallback((raw, titleHint, documentKind, opts = {}) => {
        const body = String(raw || '').trim();
        const payload = {
            agency_client_id: clientId,
            raw_text: body.slice(0, MAX_TRANSCRIPT_CHARS),
            source: DOC_SOURCE.MANUAL,
        };
        // Título: el del archivo si vino de uno; si no, uno derivado del propio texto. Sólo el
        // enlace pelado va sin título, para que el backend use el `<title>` de la página.
        const title = titleHint || deriveTitle(body);
        if (title) payload.title = title;
        // El tipo de material sólo viaja cuando la agencia lo DECLARÓ: sin declaración lo resuelve
        // el clasificador del backend, que es el caso normal. `isDeclarableKind` espeja
        // `_DECLARABLE_KINDS_ON_CREATE`; las transcripciones se detectan automáticamente.
        if (documentKind && isDeclarableKind(documentKind)) payload.document_kind = documentKind;
        if (opts.occurredOn) payload.occurred_on = opts.occurredOn;
        if (opts.businessUnitId) payload.business_unit_id = opts.businessUnitId;
        // La clave del envío. Un archivo de texto trae la suya (la de su `File`). Un texto del campo
        // reusa la de un envío idéntico que no se confirmó, y si no, estrena una. Se recuerda ANTES de
        // mandar, así un doble envío del mismo texto viaja con la misma clave.
        const signature = JSON.stringify(payload);
        const fromField = !opts.requestKey;
        const requestKey = opts.requestKey || unconfirmedTextRef.current.get(signature) || newRequestKey();
        if (fromField) rememberUnconfirmed(signature, requestKey);

        return ClientMemoryApi.createDocument(payload, {requestKey})
            .then((data) => {
                if (scopeRef.current !== clientId) return {ok: false, obsolete: true};
                const uncertain = !data || data.status === 'NETWORK_ERROR';
                // Cualquier respuesta del servidor es definitiva: entró, o no va a entrar con esa
                // clave. Sólo un corte de red deja la clave guardada para el reenvío.
                if (fromField && !uncertain) unconfirmedTextRef.current.delete(signature);
                if (data && data.status === 'OK') return {ok: true, doc: data.data};
                if (uncertain) return {ok: false, reasonKey: UNCERTAIN_KEY};
                return {ok: false, reasonKey: URL_ERROR_KEYS[data.reason_code] || 'client_memory.toast.error'};
            })
            .catch(() => ({ok: false, reasonKey: UNCERTAIN_KEY}));
    }, [clientId, rememberUnconfirmed]);

    const submitText = useCallback((raw, titleHint, documentKind) => {
        const body = String(raw || '').trim();
        if (!body) return undefined;
        start();
        return postText(body, titleHint, documentKind).then((result) => {
            if (!mountedRef.current || scopeRef.current !== clientId) return false;
            done();
            if (result.ok) {
                toast.success(t('ia_booster.omni.sent'));
                if (onIngested) onIngested(result.doc);
                return true;
            }
            toast.error(t(result.reasonKey));
            return false;
        });
    }, [clientId, done, onIngested, postText, start, t]);

    /**
     * Sube un archivo. **Devuelve una promesa de `true`/`false`**, igual que `submitText`.
     *
     * Que devuelva el desenlace no es cosmético: quien manda varias piezas en un gesto necesita
     * saber CUÁL entró. Es lo que usa la Barra Total para soltar el tipo declarado sólo si algo
     * entró de verdad, y lo que usaba el compositor para sacar de sus chips lo que ya había subido
     * —sin eso, el reintento subía el mismo archivo otra vez y quedaban dos documentos idénticos.
     *
     * El envío se cuenta como en vuelo desde ANTES de leer o subir el archivo: el `FileReader`
     * tarda, y durante esa lectura el botón quedaba habilitado.
     *
     * `documentKind` conserva el contrato opcional para callers legacy. La Barra Total no lo pide.
     * `opts` (`title`, `occurredOn`, `businessUnitId`, `durationSeconds`) es lo que una puerta
     * puede saber de más — el micrófono, por ejemplo, pone su propio título y su duración.
     */
    const ingestFile = useCallback((file, documentKind, opts = {}) => {
        if (!file) return Promise.resolve(false);
        if (scopeRef.current !== clientId) return Promise.resolve(false);
        let requestKey = fileIntentsRef.current.get(file);
        if (!requestKey) {
            requestKey = newRequestKey();
            fileIntentsRef.current.set(file, requestKey);
        }
        const verdict = classifyFile(file);
        if (verdict.mode === INGEST_MODE.REJECTED) {
            // Nombrar el archivo y decir qué SÍ entra. Un "formato no soportado" a secas deja al
            // usuario probando de nuevo con otro archivo igual.
            toast.error(t(verdict.reasonKey, {
                ...verdict.reasonParams,
                formats: formats(),
                interpolation: {escapeValue: false},
            }));
            return Promise.resolve(false);
        }

        const id = nextUploadId();
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const titleHint = opts.title || titleFromFilename(file.name);
        const retry = () => ingestFile(file, documentKind, opts);
        // Reintentar el mismo `File` reemplaza su entrada anterior (la fallada), no la duplica.
        setUploads(current => [...current.filter(entry => entry.file !== file), {
            id, file, name: file.name, kind: verdict.kind, mode: verdict.mode,
            progress: verdict.mode === INGEST_MODE.UPLOAD ? 0 : null,
            status: UPLOAD_STATUS.UPLOADING, reasonKey: null, reasonParams: null, controller, retry,
        }]);
        start();

        const settle = (result) => {
            if (!mountedRef.current || scopeRef.current !== clientId) return false;
            done();
            if (result.ok) {
                fileIntentsRef.current.delete(file);
                removeUpload(id);
                toast.success(t('ia_booster.omni.sent'));
                if (onIngested) onIngested(result.doc);
                return true;
            }
            if (result.aborted) {
                removeUpload(id);
                return false;
            }
            const reasonParams = {
                name: file.name, formats: formats(), ...(result.reasonParams || {}),
                interpolation: {escapeValue: false},
            };
            patchUpload(id, {
                status: UPLOAD_STATUS.FAILED,
                reasonKey: result.reasonKey,
                reasonParams,
                // Lo mira la tira para decidir si dibuja «Reintentar»: un formato que no entra o un
                // kill-switch apagado dan el mismo resultado por más veces que se pruebe.
                retryable: isRetryableUploadError(result),
            });
            toast.error(t(result.reasonKey, reasonParams));
            return false;
        };

        if (verdict.mode === INGEST_MODE.TEXT) {
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(postText(String(reader.result || ''), titleHint,
                    documentKind, {...opts, requestKey}));
                reader.onerror = () => resolve({ok: false, reasonKey: 'client_memory.setup.file_error'});
                reader.readAsText(file);
            }).then(settle);
        }

        let lastPercent = -1;
        const onProgress = (fraction) => {
            // Sólo al cambiar el porcentaje ENTERO: `onprogress` dispara decenas de veces por
            // segundo y cada `setUploads` re-renderiza la tira de subidas.
            const percent = Math.floor(fraction * 100);
            if (percent === lastPercent) return;
            lastPercent = percent;
            patchUpload(id, {progress: Math.min(1, fraction)});
        };
        const durationPromise = verdict.kind === FILE_KIND.AUDIO && opts.durationSeconds == null
            ? readAudioDuration(file)
            : Promise.resolve(opts.durationSeconds);

        return durationPromise
            .then(durationSeconds => ClientMemoryApi.uploadDocumentFile({
                file,
                agencyClientId: clientId,
                requestKey,
                title: titleHint,
                documentKind: documentKind && isDeclarableKind(documentKind) ? documentKind : undefined,
                occurredOn: opts.occurredOn,
                businessUnitId: opts.businessUnitId,
                durationSeconds: durationSeconds || undefined,
                onProgress,
                signal: controller ? controller.signal : undefined,
            }))
            .then((data) => {
                if (data && data.status === 'OK') return {ok: true, doc: data.data};
                if (data && data.status === 'ABORTED') return {ok: false, aborted: true};
                const reasonKey = uploadErrorKeyFor(data)
                    || (data && URL_ERROR_KEYS[data.reason_code])
                    || 'client_memory.toast.error';
                return {
                    ok: false,
                    reasonKey,
                    // Los valores del texto salen del helper compartido con la pantalla «Cargar»:
                    // tenerlos en dos lugares ya dejó `{{max}}` crudo en una de las dos.
                    reasonParams: uploadErrorParamsFor(data, {name: file.name, formats: formats()}),
                    reasonCode: data && data.reason_code,
                    errorCode: data && data.error_code,
                };
            })
            .catch(() => ({ok: false, reasonKey: 'client_memory.toast.error'}))
            .then(settle);
    }, [clientId, done, formats, onIngested, patchUpload, postText, removeUpload, start, t]);

    /**
     * Varios archivos en un gesto (drop, selector `multiple`). Resuelve
     * `[{item: file, ok}]` en el orden de entrada, con a lo sumo `UPLOAD_CONCURRENCY` en vuelo.
     */
    const ingestFiles = useCallback((files, documentKind, opts) => (
        runLimited([...(files || [])], UPLOAD_CONCURRENCY, file => ingestFile(file, documentKind, opts))
    ), [ingestFile]);

    // Soltar sobre una zona de drop: uno o varios archivos, o texto/un enlace arrastrado desde
    // otra pestaña. `documentKind` es opcional y sólo lo manda la zona que tiene selector de tipo
    // (la Barra Total): el drop sobre el orbe no declara nada, que es lo que ese gesto significa.
    const ingestDrop = useCallback((dataTransfer, documentKind) => {
        const files = dataTransfer && dataTransfer.files;
        if (files && files.length) {
            return ingestFiles(files, documentKind);
        }
        const dropped = dataTransfer && dataTransfer.getData('text');
        if (dropped) return submitText(dropped, undefined, documentKind);
        return Promise.resolve(false);
    }, [ingestFiles, submitText]);

    // Espejo del estado para leerlo desde un handler sin meter efectos en un updater de `setState`.
    const uploadsRef = useRef(uploads);
    uploadsRef.current = uploads;

    const cancelUpload = useCallback((id) => {
        const entry = uploadsRef.current.find(item => item.id === id);
        if (entry && entry.controller) entry.controller.abort();
    }, []);

    const dismissUpload = useCallback((id) => removeUpload(id), [removeUpload]);

    const binaryUploading = uploads.filter(entry => (
        entry.status === UPLOAD_STATUS.UPLOADING && entry.mode === INGEST_MODE.UPLOAD));
    const binaryInFlight = binaryUploading.length > 0;
    // Todo lo que está en vuelo MENOS las subidas binarias. La Barra Total lo usa para no congelar
    // el campo de texto ni el micrófono mientras sube un PDF: ahí el archivo viaja apenas se elige,
    // así que bloquear por `sending` deja la barra muerta 30-60 segundos al lado de su propia barra
    // de progreso — justo lo que la barra vino a evitar. Un envío de TEXTO sí bloquea: dura menos
    // de un segundo y su gesto es el mismo campo.
    const sendingText = inFlight > binaryUploading.length;
    useEffect(() => {
        if (!binaryInFlight) return undefined;
        const warn = (event) => {
            event.preventDefault();
            // El texto del diálogo lo pone el navegador; asignar `returnValue` es lo que lo abre.
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [binaryInFlight]);

    return {
        submitText, ingestFile, ingestFiles, ingestDrop, cancelUpload, dismissUpload,
        sending, sendingText, uploads,
    };
}
